import { Button, Logo } from "@rhema/ui";
import { Navigate, Outlet, useLocation, useSearchParams } from "react-router";
import { useSession } from "./session";

export function safeReturn(path: string | null | undefined): string {
  if (!path?.startsWith("/") || path.startsWith("//")) return "/chats";
  return path;
}

/**
 * Where to go once signed in. The pricing page's Choose Individual button sends people to sign up
 * with ?plan=individual, so they land on the Plan section of Settings to upgrade.
 */
export function returnPath(params: URLSearchParams): string {
  const to = params.get("return_to");
  if (to) return safeReturn(to);
  return params.get("plan") === "individual" ? "/you#plan" : "/chats";
}

/** Shown while the session loads. Only a device's first start, which creates its keys, says so. */
export function LoadingScreen({ starting = false }: { starting?: boolean }) {
  return (
    <main
      aria-busy="true"
      className="flex min-h-dvh flex-col items-center justify-center gap-5 bg-linen px-6 text-center"
    >
      <Logo size={72} variant="tile" />
      <h1 className="m-0 font-serif text-[26px] font-medium tracking-[-0.01em]">
        {starting ? "Setting up encryption on this device" : "Opening Rhema"}
      </h1>
      <p role="status" className="m-0 max-w-sm text-[15px] leading-normal text-ink-secondary">
        {starting
          ? "Your keys are made and kept on this device. The first time takes a moment."
          : "One moment while we check your session."}
      </p>
    </main>
  );
}

function ErrorScreen({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-5 bg-linen px-6 text-center">
      <Logo size={72} variant="tile" />
      <h1 className="m-0 font-serif text-[26px] font-medium tracking-[-0.01em]">Rhema could not start</h1>
      <p role="alert" className="m-0 max-w-sm text-[15px] leading-normal text-ink-secondary">
        {message}
      </p>
      <Button onClick={onRetry}>Try again</Button>
    </main>
  );
}

/** The app itself. Signed-out visitors go to sign in and come back afterwards. */
export function RequireSession() {
  const session = useSession();
  const { pathname, search } = useLocation();
  if (session.status === "signed-out") {
    return <Navigate to={`/sign-in?return_to=${encodeURIComponent(pathname + search)}`} replace />;
  }
  if (session.status === "error") {
    return <ErrorScreen message={session.error ?? ""} onRetry={session.retry} />;
  }
  if (session.status !== "ready") {
    return <LoadingScreen starting={session.status === "starting" && session.firstRun} />;
  }
  return <Outlet />;
}

/** Sign-in and sign-up. Someone already signed in goes on to where they were heading. */
export function GuestOnly() {
  const session = useSession();
  const [params] = useSearchParams();
  if (session.status === "ready") return <Navigate to={returnPath(params)} replace />;
  if (session.status === "loading" || session.status === "starting") {
    return <LoadingScreen starting={session.status === "starting" && session.firstRun} />;
  }
  return <Outlet />;
}
